'use client'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'
import { useRouter } from 'next/navigation'
import {
  FunctionComponent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import {
  MktuClass as IMktuClass,
  MktuClassWord,
} from '@/services/Api/generated'

import { Breadcrumbs } from '@/components/Breadcrumbs'
import { Button } from '@/components/Button'

import { mktuClassListStore } from '@/store/MktuClassList.store'
import { mktuSearchStore } from '@/store/MktuSearch.store'

import { MktuCLassWordList } from './components/MktuClassWordList'
import { prepareExplanationOptions } from './utils/prepareExplanationOptions'

import { Icon } from '@/icons'
import { MktuClassCategory } from '@/models/Mktu'
import { NiceSwitch } from '@/shared/Mktu/components'
import { getTypeMktuClassByNumber } from '@/shared/Mktu/utils'
import { MktuClassStore } from '@/store/Mktu/MktuClass.store'
import { HelpExpertForm } from '../HelpExpertForm'

import dynamic from 'next/dynamic'
import styles from './MktuClass.module.scss'

const Page404 = dynamic(() => import('@/modules/404').then((m) => m.Page404))
const ExplanationOption = dynamic(() =>
  import('./components/ExplanationOption').then((m) => m.ExplanationOption)
)

type MktuClassProps = {
  classNumber: IMktuClass['classNumber']
  words?: MktuClassWord[]
  mktuClass?: IMktuClass
}
export const MktuClass: FunctionComponent<MktuClassProps> = observer(
  ({ classNumber, words: initWords = [], mktuClass: initMktuClass }) => {
    const router = useRouter()

    const [visibleExplanation, setVisibleExplanation] = useState<boolean>(false)

    const [mktuClassStoreSource, setMktuClassStoreSource] = useState<
      MktuClassStore | undefined | null
    >(() => {
      if (initMktuClass != null) {
        return mktuSearchStore.getClass(classNumber, initMktuClass, initWords)
      }
      return null
    })

    // Клон исходного стора для отмены
    const [mktuClassStore, setMktuClassStore] = useState<
      MktuClassStore | undefined | null
    >(() => {
      if (mktuClassStoreSource != null) {
        return mktuClassStoreSource.clone()
      }
      return null
    })

    const [onlyNice, setOnlyNice] = useState<boolean>(false)

    useLayoutEffect(() => {
      if (initMktuClass != null) {
        const mktuClass = mktuSearchStore.getClass(
          classNumber,
          initMktuClass,
          initWords
        )
        setMktuClassStoreSource(mktuClass)
        if (mktuClass != null) {
          setMktuClassStore(mktuClass.clone())
        }
      }
    }, [classNumber, initMktuClass, initWords])

    // SET ONLY NICE
    useLayoutEffect(() => {
      if (mktuClassStore != null) {
        const searchWordsCurrentClass = mktuClassStore.words.filter((w) =>
          mktuClassStore.selectedWords.find((sW) => sW.uuid === w.uuid)
        )
        setOnlyNice(!searchWordsCurrentClass.some((w) => !w.isNice))
      }
    }, [mktuClassStore])

    const storeSelectedWords = mktuClassStore?.selectedWords

    const selectedWords = useMemo(() => {
      if (storeSelectedWords == null) {
        return []
      }

      if (onlyNice) {
        return storeSelectedWords.filter((w) => w.isNice)
      } else {
        return storeSelectedWords
      }
    }, [storeSelectedWords, onlyNice])

    const changeOnlyNice = useCallback((newOnlyNice: boolean) => {
      setOnlyNice(newOnlyNice)
    }, [])

    useLayoutEffect(() => {
      mktuClassListStore.getClasses()
    }, [classNumber])

    const apply = useCallback(() => {
      if (mktuClassStoreSource != null) {
        mktuClassStoreSource.setSelectedWords(selectedWords)
      }
      router.push('/mktu')
    }, [mktuClassStoreSource, router, selectedWords])

    const toggleVisibleExplanation = useCallback(() => {
      setVisibleExplanation((v) => !v)
    }, [])

    const goBack = useCallback(() => {
      router.push('/mktu')
    }, [router])

    const mktuClassExcluded = useMemo(() => {
      return prepareExplanationOptions(mktuClassStore?.data.excluded)
    }, [mktuClassStore])

    const mktuClassIncluded = useMemo(() => {
      return prepareExplanationOptions(mktuClassStore?.data.included)
    }, [mktuClassStore])

    const tagText = useMemo(() => {
      if (mktuClassStore == null) {
        return ''
      }
      const typeMktuClass = getTypeMktuClassByNumber(
        mktuClassStore.data.classNumber
      )
      if (typeMktuClass === MktuClassCategory.Product) {
        return 'Товары'
      } else {
        return 'Услуги'
      }
    }, [mktuClassStore])

    const breadcrumbsLinks = useMemo(
      () => [
        { url: '/', name: 'Главная' },
        {
          url: '/mktu',
          name: 'Подбор классов МКТУ',
        },
        {
          url: '/mktu/' + initMktuClass?.classNumber,
          name: `Класс ${initMktuClass?.classNumber}`,
        },
      ],
      [initMktuClass]
    )

    if (mktuClassStore == null) {
      return <Page404 />
    }

    return (
      <>
        <Breadcrumbs items={breadcrumbsLinks} className={styles.breadcrumbs} />
        <div className={styles.pageContainer}>
          <Button
            variant="opacity"
            className={cn(styles.buttonBack, styles.showBigScreen)}
            onClick={goBack}
          >
            <Icon name="arrow" className={styles.buttonBackIcon} />
            Назад к классам
          </Button>
          <div className={styles.container}>
            <Button
              variant="opacity"
              className={cn(styles.buttonBack, styles.hideBigScreen)}
              onClick={goBack}
            >
              <Icon name="arrow" className={styles.buttonBackIcon} />
              Назад к классам
            </Button>
            <div className={styles.headerContainer}>
              <div className={styles.header}>
                <h1 className={styles.title}>
                  Класс {mktuClassStore.data.classNumber}
                </h1>
                <div className={styles.type}>{tagText}</div>
              </div>
              <div className={styles.description}>
                {mktuClassStore.data.classDescription}
              </div>

              <button
                className={cn(styles.explanationButton, {
                  [styles.visibleExplanation]: visibleExplanation,
                })}
                onClick={toggleVisibleExplanation}
              >
                Пояснения к классу
                <Icon
                  name="selectArrow"
                  className={styles.explanationButtonIcon}
                />
              </button>
              {visibleExplanation ? (
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Пояснения</div>
                  <div className={styles.explanation}>
                    {mktuClassStore.data.explanation}
                  </div>
                  <div className={styles.explanationTitle}>
                    К классу относятся, в частности
                  </div>
                  <div className={styles.explanationList}>
                    {mktuClassIncluded.map((item) => (
                      <ExplanationOption key={item.join('')} data={item} />
                    ))}
                  </div>
                  <div className={styles.explanationTitle}>
                    К классу не относятся, в частности
                  </div>
                  <div className={styles.explanationList}>
                    {mktuClassExcluded.map((item) => (
                      <ExplanationOption key={item.join('')} data={item} />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            <div className={cn(styles.section, styles.listWordsSection)}>
              <div className={styles.listWordsSectionHeader}>
                <div className={styles.sectionTitle}>
                  Перечень товаров, входящих в класс
                </div>
                <NiceSwitch
                  className={styles.niceSwitch}
                  value={onlyNice}
                  onChange={changeOnlyNice}
                />
              </div>
              <MktuCLassWordList
                words={mktuClassStore.words}
                className={styles.listWords}
                selectedWords={selectedWords}
                onlyNice={onlyNice}
                onSelect={mktuClassStore.selectWord}
                onSelectAll={
                  !onlyNice
                    ? mktuClassStore.selectAllWords
                    : mktuClassStore.selectAllNiceWords
                }
                onResetAll={mktuClassStore.resetSelectedWords}
              />
              <div className={styles.footer}>
                <Button
                  variant="opacity"
                  onClick={mktuClassStore.resetSelectedWords}
                  className={styles.buttonReset}
                >
                  <Icon name="trash" /> Сбросить все
                </Button>
                <div className={styles.right}>
                  {selectedWords.length > 0 ? (
                    <span className={styles.footerInfo}>
                      Выбрано позиций в классе: {selectedWords.length}
                    </span>
                  ) : null}
                  <Button onClick={apply} className={styles.buttonApply}>
                    Применить
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <HelpExpertForm />
        </div>
      </>
    )
  }
)
